import React from 'react';
import * as Sentry from '@sentry/react';
import ErrorScreen from './ErrorScreen';
import {
    getCachedCanvasFrameAttachment,
    getCachedCanvasFrameMetadata,
    getPersistedStateAttachment,
    getPersistedStateStorageName,
} from '../../sentryAttachments';

type SentryErrorBoundaryProps = {
    children: React.ReactNode;
};

const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({ children }) => {
    return (
        <Sentry.ErrorBoundary
            fallback={({ error, componentStack, resetError }) => (
                <ErrorScreen
                    message={error instanceof Error ? error.message : 'Unexpected error'}
                    stack={componentStack}
                    onRetry={resetError}
                />
            )}
            beforeCapture={(scope) => {
                const stateAttachment = getPersistedStateAttachment();
                if (stateAttachment) {
                    scope.addAttachment(stateAttachment);
                    scope.setContext('persisted_state', {
                        storageName: getPersistedStateStorageName() ?? 'unknown',
                        size: stateAttachment.data.length,
                    });
                }

                const screenshotAttachment = getCachedCanvasFrameAttachment();
                if (screenshotAttachment) {
                    const screenshotMetadata = getCachedCanvasFrameMetadata();
                    scope.addAttachment(screenshotAttachment);
                    scope.setContext('cached_canvas_frame', {
                        capturedAt: screenshotMetadata?.capturedAt,
                        size: screenshotAttachment.data instanceof Uint8Array ? screenshotAttachment.data.byteLength : undefined,
                    });
                }
            }}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
};

export default SentryErrorBoundary;
